/**
 * Custom error classes for the SDK.
 * These provide more explicit and actionable error messages for different failure scenarios.
 */

/**
 * Error thrown when the fallback executor cannot be created in a client-side environment
 * where environment variables (like HASURA_ADMIN_SECRET) are not available.
 */
export class ClientSideExecutorError extends Error {
  constructor() {
    super(
      'Cannot create fallback executor: admin secret is not available. ' +
        'This typically occurs in client-side environments (e.g., React apps) where environment variables are not accessible. ' +
        'Please define your own executor and set it using the init() function to override the fallback executor. ' +
        'Example: init({ executor: yourCustomExecutor })'
    )
    this.name = 'ClientSideExecutorError'
  }
}

/**
 * Error thrown when a required GraphQL URL is missing.
 * This occurs when trying to create an executor without providing a URL
 * and no default URL is available in the configuration.
 */
export class MissingGraphQLUrlError extends Error {
  constructor(_options?: { providedUrl?: string; configUrl?: string }) {
    const message =
      'Cannot create GraphQL executor: missing graphqlUrl. ' +
      'Set HASURA_GRAPHQL_URL (or GQL_URL) environment variable or pass url option to createFetchExecutor().'
    super(message)
    this.name = 'MissingGraphQLUrlError'
  }
}

/**
 * Error thrown when the SDK has not been initialized and no executor is provided.
 * Call init() with an executor configuration before using SDK functions,
 * or pass an executor explicitly to each function call.
 */
export class UninitializedSDKError extends Error {
  constructor() {
    super(
      'SDK not initialized. Call init() with an executor configuration, or pass an executor explicitly. ' +
        'Example: init({ executor: createFetchExecutor({ url: "...", headers: { ... } }) })'
    )
    this.name = 'UninitializedSDKError'
  }
}

/**
 * Error thrown when a GraphQL HTTP request fails (non-OK status code).
 * This represents network-level failures or server errors.
 */
export class GraphQLHttpError extends Error {
  constructor(
    public readonly status: number,
    public readonly statusText: string,
    public readonly url?: string
  ) {
    super(
      `GraphQL request failed: ${status} ${statusText}` +
        (url ? ` (${url})` : '') +
        '. ' +
        'This may indicate a network issue, server error, or authentication problem.'
    )
    this.name = 'GraphQLHttpError'
  }
}

/**
 * Error thrown when the GraphQL server returns errors in the response.
 * This represents GraphQL-level errors (validation, authorization, business logic, etc.)
 * as opposed to HTTP-level errors.
 */
export class GraphQLServerError extends Error {
  constructor(
    public readonly errors: Array<{
      message: string
      locations?: Array<{ line: number; column: number }>
      path?: Array<string | number>
      extensions?: Record<string, unknown>
    }>
  ) {
    const messages = errors.map((e) => e.message).join(', ')
    super(
      `GraphQL server returned errors: ${messages}. ` +
        'This may indicate validation failures, authorization issues, or business logic errors.'
    )
    this.name = 'GraphQLServerError'
  }
}

/**
 * Error thrown when a GraphQL response cannot be parsed as JSON.
 * This typically indicates a malformed response from the server.
 */
export class GraphQLResponseParseError extends Error {
  public readonly cause?: Error

  constructor(public readonly originalError: unknown, public readonly url?: string) {
    const errorMessage =
      originalError instanceof Error ? originalError.message : String(originalError)
    super(
      `Failed to parse GraphQL response as JSON: ${errorMessage}` +
        (url ? ` (${url})` : '') +
        '. ' +
        'The server may have returned an invalid response format.'
    )
    this.name = 'GraphQLResponseParseError'
    if (originalError instanceof Error) {
      this.cause = originalError
    }
  }
}

/**
 * Error thrown when required SDK configuration values are missing.
 * Used by validateConfig() to indicate missing environment variables or config values.
 */
export class MissingConfigurationError extends Error {
  constructor(public readonly missingKeys: string[]) {
    super(
      `Missing required SDK configuration: ${missingKeys.join(', ')}. ` +
        'Set the corresponding environment variables or provide them via configuration.'
    )
    this.name = 'MissingConfigurationError'
  }
}

/**
 * Error thrown when a network request fails (e.g., fetch throws).
 * This represents lower-level network failures like DNS resolution, connection timeouts, etc.
 */
export class NetworkError extends Error {
  public readonly cause?: Error

  constructor(public readonly originalError: unknown, public readonly url?: string) {
    const errorMessage =
      originalError instanceof Error ? originalError.message : String(originalError)
    super(
      `Network request failed: ${errorMessage}` +
        (url ? ` (${url})` : '') +
        '. ' +
        'This may indicate a connectivity issue, DNS resolution failure, or request timeout.'
    )
    this.name = 'NetworkError'
    if (originalError instanceof Error) {
      this.cause = originalError
    }
  }
}

